/**
 * Serviço de Configurações do Sistema MEDICARE
 * Gerencia todas as operações relacionadas a configurações do sistema
 */

import { ApiService, buildQueryString } from './api';
import {
  ApiResponse,
  PaginatedResponse,
  FiltroConfiguracoes,
} from '../types';

// Interface para configuração
export interface Configuracao {
  id: string;
  chave: string;
  valor: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  categoria: string;
  descricao?: string;
  editavel: boolean;
  data_criacao: string;
  data_atualizacao: string;
}

// Interface para criar/atualizar configuração
export interface ConfiguracaoData {
  chave: string;
  valor: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  categoria: string;
  descricao?: string;
  editavel?: boolean;
}

export class ConfiguracoesService {
  private static readonly BASE_URL = '/configuracoes';

  /**
   * Listar configurações com filtros e paginação
   */
  static async listar(filtros: FiltroConfiguracoes = {}): Promise<ApiResponse<PaginatedResponse<Configuracao>>> {
    const queryString = buildQueryString(filtros);
    const url = queryString ? `${this.BASE_URL}?${queryString}` : this.BASE_URL;
    return ApiService.get<PaginatedResponse<Configuracao>>(url);
  }

  /**
   * Obter configuração por ID
   */
  static async obterPorId(id: string): Promise<ApiResponse<Configuracao>> {
    return ApiService.get<Configuracao>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Criar nova configuração
   */
  static async criar(dados: ConfiguracaoData): Promise<ApiResponse<Configuracao>> {
    return ApiService.post<Configuracao>(this.BASE_URL, dados);
  }

  /**
   * Atualizar configuração existente
   */
  static async atualizar(chave: string, dados: Partial<ConfiguracaoData>): Promise<ApiResponse<Configuracao>> {
    return ApiService.put<Configuracao>(`${this.BASE_URL}/${chave}`, dados);
  }

  /**
   * Deletar configuração
   */
  static async deletar(chave: string): Promise<ApiResponse<void>> {
    return ApiService.delete<void>(`${this.BASE_URL}/${chave}`);
  }

  /**
   * Obter configurações por categoria
   */
  static async obterPorCategoria(categoria: string): Promise<ApiResponse<Configuracao[]>> {
    return ApiService.get<Configuracao[]>(`${this.BASE_URL}/categoria/${categoria}`);
  }

  /**
   * Obter todas as categorias
   */
  static async obterCategorias(): Promise<ApiResponse<string[]>> {
    return ApiService.get<string[]>(`${this.BASE_URL}/categorias`);
  }

  /**
   * Resetar configuração para valor padrão
   */
  static async resetar(id: string): Promise<ApiResponse<Configuracao>> {
    return ApiService.post<Configuracao>(`${this.BASE_URL}/${id}/reset`);
  }

  /**
   * Backup das configurações
   */
  static async backup(): Promise<ApiResponse<Configuracao[]>> {
    return ApiService.get<Configuracao[]>(`${this.BASE_URL}/backup`);
  }

  /**
   * Restaurar configurações do backup
   */
  static async restaurar(configuracoes: Configuracao[]): Promise<ApiResponse<void>> {
    return ApiService.post<void>(`${this.BASE_URL}/restaurar`, { configuracoes });
  }
}

export default ConfiguracoesService;